import { addPromo, removePromo } from '@koala/sdk/v4';
import { call, put, select, takeLatest, type SagaReturnType } from 'redux-saga/effects';
import basketActions from '../basket/actions';
import orderStatusActions from '../orderStatus/actions';
import globalActions from '../global/actions';
import promoCodeActions from './actions';
import { fireGACommerceSelectPromotionEvent } from '@/analytics/commerce/google';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import { getOrigin } from '@/utils';
import { getIdsFromState } from '@/utils/checkout';
import { prepareErrorMessage } from '@/utils/global';
import { fireGaEvent, gaActions, gaCats } from '@/utils/googleAnalytics';
import * as ErrorReporter from '@/utils/errorReporter';
import { ERROR_MESSAGES } from '@/constants/events';

/**
 * Add promo code
 *
 */
export function* promoCodeAddSaga(action: ReturnType<typeof orderStatusActions.promoCodeAdd>) {
  try {
    const state: RootState = yield select();
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const { basketId, locationId } = getIdsFromState(state);
    if (!basketId || !locationId) {
      yield put(orderStatusActions.orderPending());
      yield put(promoCodeActions.promoCodeSet(''));
      return;
    }
    const response: SagaReturnType<typeof addPromo> = yield call(
      addPromo,
      { basketId, locationId, promoCode: action.promoCode },
      { client },
    );

    // If the promo was accepted but yielded no discount (e.g. cart below coupon minimum),
    // remove it from the server basket before committing to Redux. We must do this before
    // basketActions.success(response), otherwise the component re-sync in PromoCode
    // (promo-code/index.tsx) sees promo_codes in the basket and re-applies it.
    // Check both promo_discount (basket-level) and promo_discount_amount to cover
    // providers that populate one but not the other, and BOGO/free-item promos that
    // may express value at the line-item level only.
    const hasPromoDiscount =
      ((response.promo_discount_amount as number) ?? 0) > 0 || (response.promo_discount ?? 0) > 0;
    if ((response.promo_codes as { code: string }[] | null)?.length && !hasPromoDiscount) {
      const errorMessage = 'This promo code could not be applied to your current cart.';
      try {
        const cleanBasket: SagaReturnType<typeof removePromo> = yield call(
          removePromo,
          { basketId, locationId, promoCode: action.promoCode },
          { client },
        );
        yield put(basketActions.success(cleanBasket));
      } catch (removeError) {
        // If removal fails, commit the original response so the basket stays in sync
        yield put(basketActions.success(response));
        ErrorReporter.captureException(removeError, 'error', {
          eventName: 'REMOVE_ZERO_DISCOUNT_PROMO_FAILURE',
        });
      }
      yield put(orderStatusActions.orderPending());
      yield put(promoCodeActions.promoCodeSet(''));
      yield put(promoCodeActions.promoCodeError(errorMessage));

      fireGaEvent(gaCats.order, gaActions.couponDenied, {
        label: action.promoCode,
      });
      return;
    }

    yield put(basketActions.success(response));
    yield put(orderStatusActions.orderPending());
    yield put(promoCodeActions.promoCodeSet(action.promoCode));

    // Events
    fireGaEvent(gaCats.order, gaActions.couponApplied, {
      label: action.promoCode,
    });
    fireGACommerceSelectPromotionEvent(
      state.app.basket,
      state.app.menu.basketMenu,
      action.promoCode,
    );
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      null,
      error,
    );

    // TEAM-14743: the diner-facing rejection sentence is resolved centrally by the render rule,
    // keyed off additional_data.reason (see resolveDinerMessage's tier 0). This replaces the old
    // TEAM-13882 substring match on `detail` and no longer echoes the typed code back into the toast.
    const errorMessage = errorResponse.message;

    // Release the guard before asking for the dependent write. This saga still
    // owns `PROMO_CODE_ADDING` (type UPDATING) here, so dispatching
    // `promoCodeRemove()` first means basketMutationGuard defers it and the
    // server keeps whatever promo was applied before this failed attempt, while
    // `promoCodeSet('')` has already cleared it locally (TEAM-14696).
    yield put(orderStatusActions.orderPending());

    yield put(orderStatusActions.promoCodeRemove());
    yield put(promoCodeActions.promoCodeSet(''));
    yield put(promoCodeActions.promoCodeError(errorMessage));

    yield put(globalActions.displayErrorToast(errorMessage, false, false));

    // Events
    fireGaEvent(gaCats.order, gaActions.couponDenied, {
      label: action.promoCode,
    });
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.ADD_PROMO_FAILURE,
    });
  }
}

/**
 * Remove promo code
 *
 */
function* promoCodeRemoveSaga(action: ReturnType<typeof orderStatusActions.promoCodeRemove>) {
  try {
    const state: RootState = yield select();
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const { basketId, locationId } = getIdsFromState(state);
    if (!basketId || !locationId) {
      yield put(orderStatusActions.orderPending());
      return;
    }
    const promoCode = state.app.promoCode.applied;

    if (promoCode) {
      const response: SagaReturnType<typeof removePromo> = yield call(
        removePromo,
        { basketId, promoCode, locationId },
        { client },
      );

      yield put(basketActions.success(response));
      yield put(orderStatusActions.orderPending());
      yield put(promoCodeActions.promoCodeSet(''));

      // Events
      fireGaEvent(gaCats.order, gaActions.couponRemoved, {
        // @ts-expect-error
        label: action.promoCode,
      });
    } else {
      // No promo in state — nothing to remove. This can happen after a basket rebuild
      yield put(orderStatusActions.orderPending());
    }
  } catch (error) {
    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      null,
      error,
    );

    // yield put(globalActions.displayErrorToast(errorResponse.message, true));
    yield put(promoCodeActions.promoCodeError(errorResponse.message));
    yield put(orderStatusActions.orderPending());

    // Events
    fireGaEvent(gaCats.order, gaActions.couponRemovalFailed, {
      // @ts-expect-error
      label: action.promoCode,
    });
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.REMOVE_PROMO_FAILURE,
    });
  }
}

export default function* rootSaga() {
  yield takeLatest(orderStatusActions.PROMO_CODE_ADD, promoCodeAddSaga);
  yield takeLatest(orderStatusActions.PROMO_CODE_REMOVE, promoCodeRemoveSaga);
}
